import { Controller } from "@hotwired/stimulus"

// Horizontal carousel + cross-fade cycler for the seniors gallery.
// - Floating prev/next buttons scroll the viewport ~80% per click; native
//   scroll-snap on the track gives smooth tile-aligned stops.
// - When `images` is provided (Stimulus value), each tile runs its own
//   independent 5-10s cross-fade cycle — every tile changes regularly,
//   timing varies enough that they don't all swap in lockstep. Pool
//   must be larger than visible tiles so swaps never duplicate what's
//   currently shown.
export default class extends Controller {
  static targets = ["viewport", "prev", "next", "tile"]
  static values  = { images: Array }

  connect() {
    this.updateNavState()
    this.viewportTarget.addEventListener("scroll", this.updateNavState, { passive: true })
    window.addEventListener("resize", this.updateNavState)

    this.tileTimeouts = []
    if (this.hasImagesValue && this.imagesValue.length > 0 && this.tileTargets.length > 0) {
      // Stagger initial delays so tiles don't all fade in lockstep.
      this.tileTargets.forEach((tile, i) => {
        this.scheduleTileSwap(tile, i, 800 + i * 500)
      })
    }
  }

  disconnect() {
    this.viewportTarget.removeEventListener("scroll", this.updateNavState)
    window.removeEventListener("resize", this.updateNavState)
    if (this.tileTimeouts) this.tileTimeouts.forEach(t => clearTimeout(t))
  }

  prev() { this.viewportTarget.scrollBy({ left: -this.scrollStep, behavior: "smooth" }) }
  next() { this.viewportTarget.scrollBy({ left:  this.scrollStep, behavior: "smooth" }) }

  get scrollStep() { return this.viewportTarget.clientWidth * 0.8 }

  updateNavState = () => {
    const v = this.viewportTarget
    const atStart = v.scrollLeft <= 4
    const atEnd   = v.scrollLeft >= v.scrollWidth - v.clientWidth - 4
    if (this.hasPrevTarget) this.prevTarget.disabled = atStart
    if (this.hasNextTarget) this.nextTarget.disabled = atEnd
  }

  // ---------- Per-tile cross-fade cycler ----------

  scheduleTileSwap(tile, index, initialDelay = null) {
    // 5-10s interval per tile (or a custom initial delay for staggered start).
    const delay = initialDelay !== null ? initialDelay : (5000 + Math.random() * 5000)
    this.tileTimeouts[index] = setTimeout(() => this.swapTile(tile, index), delay)
  }

  swapTile(tile, index) {
    const layers = tile.querySelectorAll(".seniors-gallery__layer")
    if (layers.length < 2) return this.scheduleTileSwap(tile, index)

    const activeLayer   = tile.querySelector(".seniors-gallery__layer.is-active") || layers[0]
    const inactiveLayer = Array.from(layers).find(l => l !== activeLayer)
    if (!inactiveLayer) return this.scheduleTileSwap(tile, index)

    // Exclude images currently visible in OTHER tiles + this tile's current image.
    const shown = new Set(
      this.tileTargets
        .filter(t => t !== tile)
        .map(t => {
          const a = t.querySelector(".seniors-gallery__layer.is-active")
          return a ? a.getAttribute("src") : null
        })
        .filter(Boolean)
    )
    shown.add(activeLayer.getAttribute("src"))

    const available = this.imagesValue.filter(url => !shown.has(url))
    if (available.length === 0) return this.scheduleTileSwap(tile, index)

    const newUrl = available[Math.floor(Math.random() * available.length)]

    const finish = () => {
      activeLayer.classList.remove("is-active")
      inactiveLayer.classList.add("is-active")
      this.scheduleTileSwap(tile, index)
    }

    if (inactiveLayer.getAttribute("src") === newUrl && inactiveLayer.complete) {
      finish()
    } else {
      inactiveLayer.onload  = finish
      inactiveLayer.onerror = () => this.scheduleTileSwap(tile, index)
      inactiveLayer.src     = newUrl
    }
  }
}
